/**
 * Display-only currency conversion.
 *
 * IMPORTANT: The Stripe charge is ALWAYS in USD. These values exist purely to
 * show approximate local pricing on the funnel for non-US audiences. FX rates
 * are controlled static numbers (not live market rates) — update deliberately.
 * Because the displayed amount and the actual charge differ, any UI that shows a
 * converted price must also show a "billed in USD" disclosure.
 */
export interface DisplayCurrency {
    /** ISO 4217 code, e.g. 'JPY' */
    code: string;
    symbol: string;
    /** static units of this currency per 1 USD, for display conversion */
    usdRate: number;
    /** fractional digits to display (JPY = 0) */
    decimals: number;
    /** BCP-47 locale for number grouping */
    numberLocale: string;
}

export const USD: DisplayCurrency = {
    code: 'USD', symbol: '$', usdRate: 1, decimals: 2, numberLocale: 'en-US',
};

const CURRENCIES: Record<string, DisplayCurrency> = {
    USD,
    JPY: { code: 'JPY', symbol: '¥', usdRate: 155, decimals: 0, numberLocale: 'ja-JP' },
};

/** Country (ISO alpha-2) -> display currency code. Unmapped countries use USD. */
const COUNTRY_TO_CURRENCY: Record<string, string> = {
    JP: 'JPY',
};

/** Pick the display currency for a country code, defaulting to USD. */
export function displayCurrencyForCountry(countryCode: string): DisplayCurrency {
    const code = COUNTRY_TO_CURRENCY[(countryCode || '').toUpperCase()];
    return (code && CURRENCIES[code]) || USD;
}

export function isUSD(currency: DisplayCurrency): boolean {
    return currency.code === USD.code;
}

/**
 * Convert a USD amount to a display string in the given currency. USD passes
 * through with standard 2-decimal formatting; other currencies are converted
 * via the static usdRate. Pass `withSymbol: false` to get the number only.
 */
export function formatInCurrency(
    usdAmount: number,
    currency: DisplayCurrency,
    withSymbol = true,
): string {
    const converted = usdAmount * currency.usdRate;
    const formatted = converted.toLocaleString(currency.numberLocale, {
        minimumFractionDigits: currency.decimals,
        maximumFractionDigits: currency.decimals,
    });
    return withSymbol ? `${currency.symbol}${formatted}` : formatted;
}
